import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { isEqual } from 'lodash';
import { useUser } from '../../contexts/UserContext';
import SectionHeader from '../../components/shared/ui/SectionHeader';
import SectionContainer from '../../components/shared/ui/SectionContainer';
import { ErrorBoundary } from '../../components/shared/ErrorBoundary';
import RichTextEditor, { type RichTextEditorRef } from './RichTextEditor';
import { logger } from '../../utils/logger';

import { useSimpleSnapshot } from '../../hooks/useSimpleSnapshot';
import { useSnapshotScope } from '../../contexts/SnapshotScope';
import { useNotesFlush } from '../../contexts/NotesFlushContext';
import { getCurrentWeekKey, getWeekStartDateFromKey } from '../../utils/weekKeyUtils';
import { ALLOW_PAST_WEEK_EDITS } from '../../config/auth';
import { LoadErrorState } from '../../components/shared/ui/LoadErrorState';

interface NotesSectionProps {
  onChange?: (content: string) => void;
  onUpdate?: (content: string) => void;
  showToolbar?: boolean;
  className?: string;
}

/** ✅ TipTap-compliant empty document - memoized to prevent re-renders */
const DEFAULT_DOC = { type: 'doc', content: [{ type: 'paragraph' }] };

const MyNotesFallback: React.FC<{ error?: Error; resetError?: () => void }> = React.memo(({ error, resetError }) => (
  <div className="p-4 bg-red-50 text-red-700 rounded">
    <strong>Notes failed to load.</strong> Please refresh or contact support.<br/>
    {error && <pre className="mt-2 text-xs">{error.message}</pre>}
    <button onClick={resetError} className="mt-2 px-3 py-1 bg-red-600 text-white rounded">Try Again</button>
  </div>
));

MyNotesFallback.displayName = 'MyNotesFallback';

const NotesSection: React.FC<NotesSectionProps> = (({ showToolbar = true, className = '' }) => {
  const { canEditSection, isActuallyAdmin } = useUser();
  const { workspaceId, periodKey } = useSnapshotScope();
  const isAdmin = isActuallyAdmin; // Check actual role, not effective role (viewer mode)

  // Memoize defaultValue to prevent re-renders
  const memoizedDefaultValue = useMemo(() => DEFAULT_DOC, []);

  const {
    data: notesContent,              // string | object | undefined
    save,
    flushSave,
    rootKey,
    workspaceReady,
    isLoading,
    loadError
  } = useSimpleSnapshot({
    workspaceId,
    section: 'notes',
    periodKey,
    defaultValue: memoizedDefaultValue,       // stored as TipTap doc
  });

  const { registerNotesFlush } = useNotesFlush();
  useEffect(() => {
    registerNotesFlush(() => flushSave());
    return () => registerNotesFlush(null);
  }, [registerNotesFlush, flushSave]);

  /** Local editor state as a TipTap doc object */
  const [editorValue, setEditorValue] = useState<any | null>(DEFAULT_DOC); // Start with DEFAULT_DOC
  const [didUserEdit, setDidUserEdit] = useState(false);

  const prevKey = useRef(rootKey);
  const lastLoadedContentRef = useRef<any>(null);
  const lastSavedRef = useRef<any>(null); // Track last saved content to skip identical saves
  const editorValueRef = useRef<any>(editorValue); // Track latest editorValue for unmount cleanup
  const didUserEditRef = useRef(didUserEdit); // Track latest didUserEdit for unmount cleanup
  const editedWeekKeyRef = useRef<string | null>(null); // Track which week the edit was made in
  const editorRef = useRef<RichTextEditorRef>(null); // ✅ Ref to access editor instance methods
  
  // ✅ WEEK IDENTITY GUARD: Track the week this content originated from
  // This prevents cross-week writes (e.g. content from 12/15 being saved to 12/22)
  const contentOriginWeekRef = useRef<string | null>(null);
  
  // Keep refs in sync with state
  useEffect(() => {
    editorValueRef.current = editorValue;
    didUserEditRef.current = didUserEdit;
  }, [editorValue, didUserEdit]);

  // Show skeleton only for editor content area during initial load (header always visible)
  const showEditorSkeleton = !workspaceReady || (isLoading && notesContent === undefined);
  const hasCachedData = notesContent !== undefined;
  const isRefreshing = isLoading && hasCachedData;

  // ✅ FIX: Update lastSavedRef and clear edit flags when notesContent reflects a successful save
  // This ensures lastSavedRef only tracks content that was actually persisted
  // When notesContent (from hook/DB) matches editorValueRef (what user typed), save succeeded → prevent hydration overwrite
  useEffect(() => {
    if (notesContent !== undefined && editorValueRef.current) {
      if (isEqual(notesContent, editorValueRef.current)) {
        lastSavedRef.current = editorValueRef.current;
        lastLoadedContentRef.current = notesContent;
        setDidUserEdit(false);
        didUserEditRef.current = false;
        editedWeekKeyRef.current = null;
      }
    }
  }, [notesContent]);

  // Hydrate when data loads or changes - but don't overwrite user edits
  useEffect(() => {
    // ✅ WEEK IDENTITY GUARD: When we load/hydrate content, record its origin week
    // This ensures saves are always tied to the correct week
    if (notesContent !== undefined) {
      contentOriginWeekRef.current = periodKey;
    }
    
    // 🔍 PRODUCTION DEBUG: Log hydration attempt
    console.log('[Notes] Hydration effect triggered', {
      periodKey,
      rootKey,
      notesContentType: typeof notesContent,
      notesContentPreview: notesContent ? JSON.stringify(notesContent).substring(0, 100) : 'undefined',
      didUserEdit,
      didUserEditRef: didUserEditRef.current,
      editedWeekKeyRef: editedWeekKeyRef.current,
      contentOriginWeek: contentOriginWeekRef.current,
      editorValuePreview: JSON.stringify(editorValueRef.current).substring(0, 100),
      isLoading,
      hasCachedData,
      timestamp: new Date().toISOString()
    });
    
    // ✅ FIX: Hydration guard - never overwrite if user has edited in the CURRENT week
    // Check if user has edited in the current week (not a previous week)
    if (didUserEditRef.current && editedWeekKeyRef.current === rootKey) {
      // User has unsaved local edits in the current week — DO NOT hydrate / forward-fill
      console.log('[Notes] Skipping hydration - user has unsaved edits in current week');
      return;
    }
    
    // Also skip hydration if state shows user edited (double guard)
    if (didUserEdit) {
      console.log('[Notes] Skipping hydration - didUserEdit state is true');
      return;
    }
    
    // Only hydrate if:
    // 1. We have content from DB
    // 2. User hasn't made edits yet
    // 3. Content is different from what we last loaded
    // 4. Content is different from current editor value (prevent hydration loops from optimistic updates)
    if (notesContent !== undefined) {
      // Skip if notesContent matches current editorValue (prevents optimistic update loops)
      if (isEqual(notesContent, editorValueRef.current)) {
        console.log('[Notes] Skipping hydration - notesContent matches editorValue (optimistic loop prevention)');
        return;
      }
      
      // Check if content actually changed from last load
      const contentChanged = JSON.stringify(notesContent) !== JSON.stringify(lastLoadedContentRef.current);
      
      if (contentChanged) {
        console.log('[Notes] Hydrating with new content', {
          source: isLoading ? 'db' : (hasCachedData ? 'cache' : 'forwardfill'),
          contentPreview: JSON.stringify(notesContent).substring(0, 100)
        });
        
        // Handle both string and object content
        let contentToSet;
        if (typeof notesContent === 'string') {
          try {
            contentToSet = JSON.parse(notesContent);
          } catch {
            contentToSet = DEFAULT_DOC;
          }
        } else if (notesContent && typeof notesContent === 'object') {
          contentToSet = notesContent;
        } else {
          contentToSet = DEFAULT_DOC;
        }

        console.log('[Notes] Setting editor value', {
          contentToSetPreview: JSON.stringify(contentToSet).substring(0, 100)
        });
        setEditorValue(contentToSet);
        lastLoadedContentRef.current = notesContent;
      } else {
        console.log('[Notes] Skipping hydration - content unchanged from last load');
      }
    } else {
      console.log('[Notes] notesContent is undefined - not hydrating');
    }
  }, [notesContent, rootKey, didUserEdit, isLoading, hasCachedData, periodKey]);

  const canEdit = canEditSection('notes');
  
  // Check if current week, future week, or past week editing is allowed
  const currentWeekKey = getCurrentWeekKey();
  const currentWeekDate = getWeekStartDateFromKey(currentWeekKey);
  const periodDate = getWeekStartDateFromKey(periodKey);
  const isCurrentWeek = periodKey === currentWeekKey;
  const isFutureWeek = currentWeekDate && periodDate && periodDate.getTime() > currentWeekDate.getTime();
  const isPastWeek = currentWeekDate && periodDate && periodDate.getTime() < currentWeekDate.getTime();
  
  // Admins can always edit past weeks (for cumulative dashboard, mistake corrections, etc.)
  // Viewers can only edit current/future weeks (or past weeks if explicitly enabled)
  const isEditable = canEdit && (isCurrentWeek || isFutureWeek || (isPastWeek && (isAdmin || ALLOW_PAST_WEEK_EDITS)));

  /** Editor → state (always store a TipTap doc object) */
  // ✅ SIMPLIFIED: Notes now works like other sections - just call save() and let useSimpleSnapshot handle everything
  const handleChange = useCallback((content: any, shouldFlush = false) => {
    // Guard: only save if editable (double-check to prevent viewer mode errors)
    if (!isEditable) {
      // In viewer mode, just update local state for display, don't save
      setEditorValue(content);
      return;
    }
    
    // ✅ WEEK IDENTITY GUARD: Verify we're still on the same week before saving
    // This prevents cross-week writes when periodKey changes during editing
    const currentPeriodKey = periodKey;
    const originWeek = contentOriginWeekRef.current;
    
    if (originWeek && originWeek !== currentPeriodKey) {
      // Week changed between load and save → block save to prevent cross-week write
      console.error('[Notes] WEEK IDENTITY MISMATCH - blocking save', {
        contentOriginWeek: originWeek,
        currentPeriodKey,
        rootKey,
        timestamp: new Date().toISOString()
      });
      
      logger.error('Week identity mismatch detected - blocking cross-week save', {
        contentOriginWeek: originWeek,
        currentPeriodKey,
        rootKey
      }, 'NotesSection');
      
      // Record in telemetry for production monitoring
      try {
        const w = typeof window !== 'undefined' ? window : null;
        if (w && typeof w === 'object') {
          const marlo = (w as Window & { __marlo?: any }).__marlo || {};
          marlo.weekIdentityMismatch = {
            contentOriginWeek: originWeek,
            attemptedSaveWeek: currentPeriodKey,
            timestamp: new Date().toISOString()
          };
          (w as Window & { __marlo?: any }).__marlo = marlo;
        }
      } catch {
        // Telemetry failures are non-critical
      }
      
      return; // Block save
    }
    
    // 🔍 PRODUCTION DEBUG: Log what we're about to save
    console.log('[Notes] handleChange called', {
      periodKey,
      rootKey,
      contentOriginWeek: originWeek,
      contentPreview: JSON.stringify(content).substring(0, 100),
      shouldFlush,
      timestamp: new Date().toISOString()
    });
    
    // Update local state immediately for responsive UI
    setDidUserEdit(true);
    setEditorValue(content);
    // ✅ CRITICAL: Keep refs in sync immediately (avoid missing last keystroke on unmount)
    // useEffect may not run before unmount when switching tabs quickly
    editorValueRef.current = content;
    didUserEditRef.current = true;
    editedWeekKeyRef.current = rootKey; // Track which week the edit was made in
    contentOriginWeekRef.current = currentPeriodKey; // Update origin week on edit
    
    // ✅ CRITICAL: Flush immediately when user clicks away (blur event)
    // This ensures deletions and edits are saved before refresh
    if (shouldFlush) {
      console.log('[Notes] Flushing immediately (bypass debounce)', { periodKey, rootKey });
      flushSave().catch((error) => {
        console.error('[Notes] Flush failed', { error, section: 'notes', periodKey });
        logger.error('Failed to flush notes', { error, section: 'notes', periodKey }, 'NotesSection');
      });
    } else {
      // Call save - useSimpleSnapshot handles debouncing, caching, and persistence
      // Notes uses 300ms debounce (same as configured in useSimpleSnapshot)
      save(content).catch((error) => {
        console.error('[Notes] Save failed', { error, section: 'notes', periodKey });
        logger.error('Failed to save notes', { error, section: 'notes', periodKey }, 'NotesSection');
      });
    }
  }, [isEditable, save, flushSave, periodKey, rootKey]);

  // Reset state when rootKey changes (week/section change)
  // Fire-and-forget flush (don't block navigation)
  useEffect(() => {
    if (prevKey.current !== rootKey) {
      // Fire-and-forget flush if user edited
      if (didUserEdit && editorValue) {
        flushSave().catch((error: unknown) => {
          logger.error('Failed to flush save on week change', { error, section: 'notes', periodKey }, 'NotesSection');
        });
      }
      
      // Clear flags immediately (don't wait for save)
      setDidUserEdit(false);
      didUserEditRef.current = false;
      editedWeekKeyRef.current = null;
      lastLoadedContentRef.current = null;
      lastSavedRef.current = null;
      
      prevKey.current = rootKey;
    }
  }, [rootKey, didUserEdit, editorValue, flushSave, periodKey]);

  // Unmount cleanup: fire-and-forget save (handleTabChange already triggered flush)
  useEffect(() => {
    return () => {
      // ✅ Note: handleTabChange already called flushSave() when leaving Notes
      // This is a safety net only for edge cases (browser close, direct URL nav, etc.)
      const shouldSave = didUserEditRef.current;
      if (shouldSave) {
        const latestContent = editorRef.current?.getLatestContent() ?? editorValueRef.current;
        if (latestContent && !isEqual(latestContent, lastSavedRef.current)) {
          save(latestContent).catch((error: unknown) => {
            logger.error('Failed to save on unmount', { error, section: 'notes', periodKey }, 'NotesSection');
          });
        }
      }
    };
  }, [save, periodKey]);

  const editorProps = useMemo(() => ({
    placeholder: "Start typing your notes...",
    isInternalUser: true,
    readOnly: !isEditable,
    showToolbar,
    className: "notes-editor"
  }), [isEditable, showToolbar]); // Fix: use isEditable instead of canEdit

  
  return (
    <SectionContainer 
      variant="bare" 
      className={`${className} ${hasCachedData ? 'animate-fadeIn' : ''} relative`}
      testId="section-notes" 
      aria-label="Notes section"
    >
      {/* Header always visible - outside skeleton wrapper */}
      <div className="notes-panel elevation-card">
        <SectionHeader title="Notes" />
        {/* Editor content - Always render editor, skeleton overlay during load, smooth fade-in for content */}
        <div className="relative min-h-[320px]">
          {/* Error state if workspace failed to resolve */}
          {loadError && (
            <LoadErrorState
              error={loadError}
              onRetry={() => window.location.reload()}
              onRefresh={() => window.location.reload()}
              sectionName="Notes"
            />
          )}
          {/* Skeleton overlay during initial load */}
          {!loadError && showEditorSkeleton && (
            <div className="absolute inset-0 bg-[var(--bg)] bg-opacity-50 z-10 flex items-center justify-center pointer-events-none">
              <div className="opacity-75 w-full px-4 py-3 space-y-2">
                <div className="h-6 bg-surface rounded animate-pulse w-full"></div>
                <div className="h-6 bg-surface rounded animate-pulse w-11/12"></div>
                <div className="h-6 bg-surface rounded animate-pulse w-10/12"></div>
                <div className="h-6 bg-surface rounded animate-pulse w-full"></div>
                <div className="h-6 bg-surface rounded animate-pulse w-9/12"></div>
              </div>
            </div>
          )}
          {/* Refresh overlay during data refresh */}
          {!loadError && isRefreshing && !showEditorSkeleton && (
            <div className="absolute inset-0 bg-[var(--bg)] bg-opacity-50 z-10 flex items-center justify-center pointer-events-none">
              <div className="opacity-75 w-full px-4 py-3 space-y-2">
                <div className="h-6 bg-surface rounded animate-pulse w-full"></div>
                <div className="h-6 bg-surface rounded animate-pulse w-11/12"></div>
                <div className="h-6 bg-surface rounded animate-pulse w-10/12"></div>
                <div className="h-6 bg-surface rounded animate-pulse w-full"></div>
                <div className="h-6 bg-surface rounded animate-pulse w-9/12"></div>
              </div>
            </div>
          )}
          {/* Editor always visible - smooth fade-in animation for content */}
          {!loadError && (
            <div className={`transition-all duration-600 ease-out ${hasCachedData ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
              <ErrorBoundary fallback={MyNotesFallback}>
              {!editorValue ? (
                <div className="notes-editor">
                  <div className="notes-editor-body">
                    <div className="min-h-[320px] flex items-center justify-center theme-text-muted">
                      No content loaded. editorValue: {JSON.stringify(editorValue)}
                    </div>
                  </div>
                </div>
              ) : (
                <RichTextEditor
                  ref={editorRef}
                  key={rootKey} // ✅ ensure rerender per week/workspace
                  value={editorValue}
                  onChange={handleChange}
                  {...editorProps}
                  className="notes-editor"
                />
              )}
              </ErrorBoundary>
            </div>
          )}
        </div>
      </div>
    </SectionContainer>
  );
});

NotesSection.displayName = 'NotesSection';

export default NotesSection;